import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Package, 
  RefreshCw, 
  Gift,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  Mail,
  MailX,
  ArrowRight,
  TrendingUp,
  Wallet
} from 'lucide-react';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, BarChart, Bar
} from 'recharts';
import { getAdminStats, getAdminReturns, getAdminRMAs } from '@/lib/api';
import { format, parseISO, subDays, subWeeks, subMonths } from 'date-fns';
import { it } from 'date-fns/locale';
import { cn } from '@/lib/utils';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const [returns, setReturns] = useState([]);
  const [rmas, setRMAs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('week');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [returnsData, rmasData] = await Promise.all([
        getAdminReturns(),
        getAdminRMAs()
      ]);
      setReturns(returnsData || []);
      setRMAs(rmasData || []);
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setIsLoading(false);
  };

  const allRequests = useMemo(() => [...returns, ...rmas], [returns, rmas]);

  // Action-needed breakdown
  const actionNeeded = useMemo(() => {
    const ACTIVE_STATES = ['RICHIESTA', 'RICHIESTA_DA_VERIFICARE', 'IN_ATTESA_DI_ARRIVO', 'CAMBIO_IN_LAVORAZIONE', 'pending', 'requested', 'approved', 'processing'];
    const active = allRequests.filter(r => ACTIVE_STATES.includes(r.status));
    
    const waitingApproval = allRequests.filter(r => ['RICHIESTA', 'RICHIESTA_DA_VERIFICARE', 'pending', 'requested'].includes(r.status));
    const waitingArrival = allRequests.filter(r => r.status === 'IN_ATTESA_DI_ARRIVO');
    const inProgress = allRequests.filter(r => ['CAMBIO_IN_LAVORAZIONE', 'processing', 'approved'].includes(r.status));
    
    // Failed emails
    const failedEmails = allRequests.filter(r => {
      const log = r.email_log || [];
      return log.some(e => !e.success);
    });

    return { total: active.length, waitingApproval, waitingArrival, inProgress, failedEmails };
  }, [allRequests]);

  // KPIs
  const kpiData = useMemo(() => {
    const total = allRequests.length;
    const completed = allRequests.filter(r => ['COMPLETATO', 'completed'].includes(r.status)).length;
    const refundValue = rmas.reduce((sum, rma) => sum + (rma.refund_amount || 0), 0);
    const conversions = returns.length;
    const conversionRate = total > 0 ? Math.round((conversions / total) * 100) : 0;
    return { total, completed, refundValue, conversionRate };
  }, [allRequests, returns, rmas]);

  // Chart data
  const chartData = useMemo(() => {
    let startDate;
    let dateFormat;
    switch (timeRange) {
      case 'day': startDate = subDays(new Date(), 7); dateFormat = 'EEE'; break;
      case 'week': startDate = subWeeks(new Date(), 4); dateFormat = "'Sett.' w"; break;
      case 'month': startDate = subMonths(new Date(), 6); dateFormat = 'MMM'; break;
      case 'year': startDate = subMonths(new Date(), 12); dateFormat = 'MMM yy'; break;
      default: startDate = subWeeks(new Date(), 4); dateFormat = "'Sett.' w";
    }
    const grouped = {};
    allRequests.forEach(req => {
      try {
        const date = parseISO(req.created_at);
        if (date >= startDate) {
          const key = format(date, dateFormat, { locale: it });
          if (!grouped[key]) grouped[key] = { name: key, cambi: 0, voucher: 0, resi: 0, total: 0 };
          if (req.rma_id) grouped[key].resi++;
          else if (req.request_type === 'exchange') grouped[key].cambi++;
          else if (req.request_type === 'voucher') grouped[key].voucher++;
          grouped[key].total++;
        }
      } catch (e) { /* skip invalid dates */ }
    });
    return Object.values(grouped);
  }, [allRequests, timeRange]);

  // Type distribution
  const typeDistribution = useMemo(() => {
    const cambi = returns.filter(r => r.request_type === 'exchange').length;
    const voucher = returns.filter(r => r.request_type === 'voucher').length;
    const resi = rmas.length;
    return [
      { name: 'Cambi', value: cambi, color: '#3b82f6' },
      { name: 'Voucher', value: voucher, color: '#10b981' },
      { name: 'Resi', value: resi, color: '#f59e0b' }
    ].filter(item => item.value > 0);
  }, [returns, rmas]);

  // Reason distribution
  const reasonDistribution = useMemo(() => {
    const reasons = {};
    rmas.forEach(rma => {
      rma.items?.forEach(item => {
        const reason = item.q3_return_reason;
        if (reason) { reasons[reason] = (reasons[reason] || 0) + 1; }
      });
    });
    const labels = {
      'too_small': 'Troppo piccolo', 'too_large': 'Troppo grande',
      'different_fit': 'Vestibilità diversa', 'different_color': 'Colore diverso',
      'defect': 'Difetto', 'changed_mind': 'Ripensamento', 'other': 'Altro'
    };
    return Object.entries(reasons).map(([key, value], idx) => ({
      name: labels[key] || key, value, color: COLORS[idx % COLORS.length]
    }));
  }, [rmas]);

  // Recent activity
  const recentActivity = useMemo(() => {
    const sorted = [...allRequests].sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));
    return sorted.slice(0, 6).map(r => {
      const type = r.rma_id ? 'rma' : r.request_type;
      const typeLabels = { exchange: 'Cambio', voucher: 'Voucher', rma: 'Reso' };
      const statusLabels = {
        RICHIESTA: 'Nuova richiesta', IN_ATTESA_DI_ARRIVO: 'In attesa arrivo',
        CAMBIO_IN_LAVORAZIONE: 'In lavorazione', COMPLETATO: 'Completato',
        RESO_RIFIUTATO: 'Rifiutato', ARCHIVIATO: 'Archiviato',
      };
      return {
        id: r.id || r.rma_id,
        order: r.order_number,
        customer: r.customer_name,
        type: typeLabels[type] || type,
        status: statusLabels[r.status] || r.status,
        rawStatus: r.status,
        date: r.updated_at || r.created_at,
      };
    });
  }, [allRequests]);

  if (isLoading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-xl lg:text-2xl font-heading font-semibold text-gray-900">Dashboard</h1>
          <p className="text-gray-500 text-sm">Panoramica delle richieste di reso e cambio</p>
        </div>
        <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg p-1 self-start sm:self-auto">
          {[
            { id: 'day', label: 'Giorni' },
            { id: 'week', label: 'Settimane' },
            { id: 'month', label: 'Mesi' },
            { id: 'year', label: 'Anno' }
          ].map(item => (
            <button
              key={item.id}
              onClick={() => setTimeRange(item.id)}
              className={cn(
                "px-3 py-1.5 text-xs sm:text-sm rounded-md transition-all",
                timeRange === item.id ? "bg-primary text-white" : "text-gray-600 hover:bg-gray-100"
              )}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {/* Action Needed Banner */}
      {actionNeeded.total > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-5"
          data-testid="action-needed-banner"
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-amber-900 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              {actionNeeded.total} richieste da gestire
            </h3>
            <button
              onClick={() => navigate('/admindea26/resi')}
              className="text-sm text-amber-700 hover:text-amber-900 flex items-center gap-1 font-medium"
            >
              Vedi tutte <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {actionNeeded.waitingApproval.length > 0 && (
              <div className="bg-white/70 rounded-lg p-3 border border-amber-200/50">
                <p className="text-2xl font-bold text-amber-700">{actionNeeded.waitingApproval.length}</p>
                <p className="text-xs text-amber-600">Da approvare</p>
              </div>
            )}
            {actionNeeded.waitingArrival.length > 0 && (
              <div className="bg-white/70 rounded-lg p-3 border border-blue-200/50">
                <p className="text-2xl font-bold text-blue-700">{actionNeeded.waitingArrival.length}</p>
                <p className="text-xs text-blue-600">In attesa arrivo</p>
              </div>
            )}
            {actionNeeded.inProgress.length > 0 && (
              <div className="bg-white/70 rounded-lg p-3 border border-purple-200/50">
                <p className="text-2xl font-bold text-purple-700">{actionNeeded.inProgress.length}</p>
                <p className="text-xs text-purple-600">In lavorazione</p>
              </div>
            )}
            {actionNeeded.failedEmails.length > 0 && (
              <div className="bg-white/70 rounded-lg p-3 border border-red-200/50">
                <p className="text-2xl font-bold text-red-700">{actionNeeded.failedEmails.length}</p>
                <p className="text-xs text-red-600">Email fallite</p>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Richieste Totali', value: kpiData.total, icon: Package, iconBg: 'bg-blue-100', iconColor: 'text-blue-600' },
          { label: 'Completate', value: kpiData.completed, icon: CheckCircle, iconBg: 'bg-emerald-100', iconColor: 'text-emerald-600' },
          { label: 'Tasso Conversione', value: `${kpiData.conversionRate}%`, icon: TrendingUp, iconBg: 'bg-violet-100', iconColor: 'text-violet-600', sub: 'Cambi/Voucher vs Resi' },
          { label: 'Valore Rimborsi', value: `EUR ${kpiData.refundValue.toFixed(0)}`, icon: Wallet, iconBg: 'bg-red-100', iconColor: 'text-red-600' },
        ].map((kpi, idx) => (
          <motion.div
            key={kpi.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.05 }}
            className="bg-white rounded-xl p-5 border border-gray-200"
          >
            <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center mb-3", kpi.iconBg)}>
              <kpi.icon className={cn("w-5 h-5", kpi.iconColor)} />
            </div>
            <p className="text-2xl font-bold text-gray-900">{kpi.value}</p>
            <p className="text-sm text-gray-500 mt-0.5">{kpi.label}</p>
            {kpi.sub && <p className="text-xs text-gray-400">{kpi.sub}</p>}
          </motion.div>
        ))}
      </div>

      {/* Charts + Activity Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Main Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="lg:col-span-2 bg-white rounded-xl p-6 border border-gray-200"
        >
          <h3 className="text-base font-semibold text-gray-900 mb-5">Andamento Richieste</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorCambi" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorVoucher" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.2}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorResi" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.2}/>
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#9ca3af" />
                <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" allowDecimals={false} />
                <Tooltip
                  contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}
                />
                <Area type="monotone" dataKey="cambi" stroke="#3b82f6" fill="url(#colorCambi)" name="Cambi" strokeWidth={2} />
                <Area type="monotone" dataKey="voucher" stroke="#10b981" fill="url(#colorVoucher)" name="Voucher" strokeWidth={2} />
                <Area type="monotone" dataKey="resi" stroke="#f59e0b" fill="url(#colorResi)" name="Resi" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Recent Activity */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl border border-gray-200 overflow-hidden"
        >
          <div className="px-5 py-4 border-b border-gray-100">
            <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <Clock className="w-4 h-4 text-gray-400" />
              Attività Recenti
            </h3>
          </div>
          <div className="divide-y divide-gray-50">
            {recentActivity.map((item) => {
              const statusColors = {
                COMPLETATO: 'text-emerald-600', completed: 'text-emerald-600',
                RESO_RIFIUTATO: 'text-red-600', rejected: 'text-red-600',
                ARCHIVIATO: 'text-gray-500',
              };
              const color = statusColors[item.rawStatus] || 'text-amber-600';
              let dateStr = '';
              try { 
                const hasTz = /[Zz]|[+-]\d{2}:?\d{2}$/.test(item.date || '');
                const d = new Date(hasTz ? item.date : item.date + 'Z');
                dateStr = d.toLocaleString('it-IT', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome' });
              } catch (e) { dateStr = ''; }

              return (
                <div
                  key={item.id}
                  className="px-5 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/admindea26/resi/${item.id}`)}
                  data-testid={`activity-${item.id}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900 truncate">#{item.order}</span>
                    <span className="text-xs text-gray-400 flex-shrink-0 ml-2">{dateStr}</span>
                  </div>
                  <div className="flex items-center justify-between mt-0.5">
                    <span className="text-xs text-gray-500">{item.customer} &middot; {item.type}</span>
                    <span className={cn("text-xs font-medium", color)}>{item.status}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>

      {/* Bottom Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pie Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl p-6 border border-gray-200"
        >
          <h3 className="text-base font-semibold text-gray-900 mb-5">Distribuzione Richieste</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={typeDistribution} cx="50%" cy="50%" innerRadius={55} outerRadius={75} paddingAngle={5} dataKey="value">
                  {typeDistribution.map((entry, index) => (
                    <Cell key={`cell-${entry.name}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Reasons Chart */}
        {reasonDistribution.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white rounded-xl p-6 border border-gray-200"
          >
            <h3 className="text-base font-semibold text-gray-900 mb-5">Motivi di Reso</h3>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={reasonDistribution} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 11 }} stroke="#9ca3af" allowDecimals={false} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} stroke="#9ca3af" width={110} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#3b82f6" radius={[0, 4, 4, 0]} name="Richieste" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboardPage;
